import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./rightClickMenu.scss";
import classNames from "classnames";
import Portal from "elements/Portal";

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;

interface IMenuAction {
  icon: IconName;
  iconColor?: string;
  title: string;
  disabled?: boolean;
  action: () => void;
}

interface IRightClickMenuProps {
  children: React.ReactNode;
  sections: Array<Array<IMenuAction>>;
  informationSection?: Array<string | JSX.Element>;
  fullHeightWidth?: boolean;
  disableAllActions?: boolean;
  alignment?: "left" | "right";
}

interface IMousePosition {
  x: number;
  y: number;
}

interface IRightClickMenuState {
  open: boolean;
  mousePosition: IMousePosition;
}

export default function RightClickMenu(props: IRightClickMenuProps) {
  const { children, sections, informationSection, fullHeightWidth, disableAllActions, alignment = "left" } = props;

  const RIGHT_CLICK_MENU_CONTAINER_CLASS_NAME = "right-click-menu-container";

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressOriginRef = useRef<IMousePosition | null>(null);
  const suppressClickRef = useRef(false);
  const openedByLongPressRef = useRef(false);

  const [state, setState] = useState<IRightClickMenuState>({
    open: false,
    mousePosition: {
      x: 0,
      y: 0,
    },
  });

  function closeMenu() {
    setState(prev => ({
      ...prev,
      open: false,
      mousePosition: {
        x: 0,
        y: 0,
      },
    }));
  }

  function openMenuAt(x: number, y: number) {
    setState(prev => ({
      ...prev,
      open: true,
      mousePosition: { x, y },
    }));
  }

  function clearLongPress() {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressOriginRef.current = null;
  }

  function armClickSuppression() {
    suppressClickRef.current = true;
    if (suppressClickTimeoutRef.current !== null) {
      clearTimeout(suppressClickTimeoutRef.current);
    }
    suppressClickTimeoutRef.current = setTimeout(() => {
      suppressClickRef.current = false;
      suppressClickTimeoutRef.current = null;
    }, 500);
  }

  function handleClick(e: MouseEvent) {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!menuContainerRef?.current) {
      return;
    }

    const target: Node = e.target as Node;
    const rightClicked = e.button === 2;

    const clickedInsideMenuContainer = menuContainerRef.current.contains(target);
    const clickedInsideMenu = menuRef.current?.contains(target) ?? false;

    if (!clickedInsideMenuContainer && !clickedInsideMenu) {
      if (state.open) {
        closeMenu();
      }

      return;
    }

    if (clickedInsideMenu) {
      return;
    }

    const rightClickMenuContainers = Array.from(
      menuContainerRef.current.querySelectorAll(`.${RIGHT_CLICK_MENU_CONTAINER_CLASS_NAME}`),
    );

    const clickedNestedRightClickMenu = rightClickMenuContainers.some(container => container.contains(target));

    if (clickedNestedRightClickMenu) {
      closeMenu();
      return;
    }

    const clickedInEmptyContainerSpace = clickedInsideMenuContainer;

    if (clickedInEmptyContainerSpace) {
      if (rightClicked) {
        openMenuAt(e.clientX, e.clientY);
        return;
      } else {
        closeMenu();
        return;
      }
    }
  }

  function handleSuppressClick(e: MouseEvent) {
    if (!suppressClickRef.current) {
      return;
    }

    suppressClickRef.current = false;
    if (suppressClickTimeoutRef.current !== null) {
      clearTimeout(suppressClickTimeoutRef.current);
      suppressClickTimeoutRef.current = null;
    }
    e.preventDefault();
    e.stopPropagation();
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== "touch" || e.button !== 0) {
      return;
    }

    clearLongPress();
    openedByLongPressRef.current = false;
    longPressOriginRef.current = { x: e.clientX, y: e.clientY };

    longPressTimerRef.current = setTimeout(() => {
      const origin = longPressOriginRef.current;
      if (!origin) {
        return;
      }

      openedByLongPressRef.current = true;
      openMenuAt(origin.x, origin.y);
      clearLongPress();
    }, LONG_PRESS_MS);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const origin = longPressOriginRef.current;
    if (!origin || e.pointerType !== "touch") {
      return;
    }

    const dx = e.clientX - origin.x;
    const dy = e.clientY - origin.y;
    if (dx * dx + dy * dy > LONG_PRESS_MOVE_THRESHOLD_PX * LONG_PRESS_MOVE_THRESHOLD_PX) {
      clearLongPress();
    }
  }

  function handlePointerEnd() {
    if (openedByLongPressRef.current) {
      armClickSuppression();
      openedByLongPressRef.current = false;
    }
    clearLongPress();
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("click", handleSuppressClick, true);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("click", handleSuppressClick, true);
    };
  }, [state.open]);

  useLayoutEffect(() => {
    if (!state.open || !menuRef.current) {
      return;
    }

    const menuRect = menuRef.current.getBoundingClientRect();
    const overflowBottom = menuRect.bottom - window.innerHeight;

    if (overflowBottom <= 0) {
      return;
    }

    const nextY = Math.max(0, state.mousePosition.y - overflowBottom);
    if (nextY === state.mousePosition.y) {
      return;
    }

    setState(prev => ({
      ...prev,
      mousePosition: {
        ...prev.mousePosition,
        y: nextY,
      },
    }));
  }, [state.open, state.mousePosition.x, state.mousePosition.y, sections, informationSection]);

  useEffect(() => {
    return () => {
      clearLongPress();
      if (suppressClickTimeoutRef.current !== null) {
        clearTimeout(suppressClickTimeoutRef.current);
      }
    };
  }, []);

  function handleAction(menuAction: IMenuAction) {
    if (menuAction?.action) {
      menuAction.action();
    }

    closeMenu();
  }

  return (
    <div
      ref={menuContainerRef}
      className={classNames(RIGHT_CLICK_MENU_CONTAINER_CLASS_NAME, {
        "right-click-menu-container_full": fullHeightWidth,
      })}
      onContextMenu={e => e.preventDefault()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      {children}
      <Portal isMounted={state.open}>
        <div
          ref={menuRef}
          className="right-click-menu"
          style={{
            display: state.open ? "block" : "none",
            left: state.mousePosition.x,
            top: state.mousePosition.y,
            transform: alignment === "right" ? "translateX(-100%)" : "",
          }}
          onClick={e => e.stopPropagation()}
          draggable="true"
          onDragStart={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="right-click-menu-scroll">
            {sections?.map((section, sectionIndex) => {
              return (
                <React.Fragment key={sectionIndex}>
                  <div className="right-click-menu-section">
                    {section?.map((menuAction, actionIndex) => {
                      return (
                        <button
                          key={actionIndex}
                          onClick={() => handleAction(menuAction)}
                          className="right-click-menu-section-action"
                          disabled={menuAction.disabled || disableAllActions}
                        >
                          <FontAwesomeIcon
                            className="right-click-menu-section-action-icon"
                            color={menuAction.iconColor}
                            icon={["far", menuAction.icon]}
                          />
                          <div>{menuAction.title}</div>
                        </button>
                      );
                    })}
                  </div>
                  {sectionIndex < sections.length - 1 && <hr className="right-click-menu-section-divider" />}
                </React.Fragment>
              );
            })}
            {informationSection && informationSection.length > 0 && (
              <>
                {sections?.length > 0 && <hr className="right-click-menu-section-divider" />}

                <div className="right-click-menu-information-section">
                  {informationSection.map((information, index) => {
                    if (
                      React.isValidElement(information) &&
                      information.type === "hr"
                    ) {
                      return React.cloneElement(information, { key: index });
                    }

                    return <div key={index}>{information}</div>;
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </Portal>
    </div>
  );
}
